import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Space,
  DatePicker,
  Input,
  Divider,
  Select,
  InputNumber,
  Button,
  Typography,
  Alert,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  JournalLineItem,
  calculateLineTotals,
  formatCurrency,
} from './journalEntriesHelpers';

const { Text } = Typography;
const { Option } = Select;

export interface CreateJournalEntryModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (payload: {
    date: string;
    description: string;
    is_adjustment: boolean;
    lines: Array<{
      account_id: number;
      contact_id: number | null;
      description: string;
      debit: number;
      credit: number;
    }>;
  }) => Promise<void>;
  accounts: any[];
  contacts: any[];
  lockDate?: string | null;
  loading?: boolean;
}

export const CreateJournalEntryModal: React.FC<CreateJournalEntryModalProps> = ({
  visible,
  onClose,
  onSave,
  accounts,
  contacts,
  lockDate,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [entryLines, setEntryLines] = useState<JournalLineItem[]>([
    { account_id: null, contact_id: null, description: '', debit: 0, credit: 0 },
    { account_id: null, contact_id: null, description: '', debit: 0, credit: 0 },
  ]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({
        date: dayjs(),
        description: '',
        is_adjustment: false,
      });
      setEntryLines([
        { account_id: null, contact_id: null, description: '', debit: 0, credit: 0 },
        { account_id: null, contact_id: null, description: '', debit: 0, credit: 0 },
      ]);
    }
  }, [visible, form]);

  const { totalDebit, totalCredit, difference, isBalanced } = calculateLineTotals(entryLines);

  const handleLineChange = (index: number, field: keyof JournalLineItem, value: any) => {
    const updated = [...entryLines];
    (updated[index] as any)[field] = value;
    setEntryLines(updated);
  };

  const handleAddLine = () => {
    setEntryLines([
      ...entryLines,
      { account_id: null, contact_id: null, description: '', debit: 0, credit: 0 },
    ]);
  };

  const handleRemoveLine = (index: number) => {
    if (entryLines.length <= 2) {
      message.warning('Un comprobante contable debe contener al menos dos partidas.');
      return;
    }
    const updated = entryLines.filter((_, i) => i !== index);
    setEntryLines(updated);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!isBalanced) {
        message.error(
          `El asiento contable está descuadrado por ${formatCurrency(
            Math.abs(difference)
          )}. La suma de débitos debe ser igual a la de créditos.`
        );
        return;
      }

      const invalidLine = entryLines.some(
        (l) =>
          !l.account_id ||
          (Number(l.debit) === 0 && Number(l.credit) === 0)
      );
      if (invalidLine) {
        message.error(
          'Todas las líneas deben tener una cuenta seleccionada y un valor en Débito o Crédito.'
        );
        return;
      }

      await onSave({
        date: values.date.format('YYYY-MM-DD'),
        description: values.description,
        is_adjustment: values.is_adjustment || false,
        lines: entryLines.map((l) => ({
          account_id: l.account_id as number,
          contact_id: l.contact_id || null,
          description: l.description || values.description,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        })),
      });
    } catch (err: any) {
      if (err?.errorFields) return; // Form validation error
      console.error('Error creating journal entry:', err);
    }
  };

  return (
    <Modal
      title="Registrar Nuevo Asiento Contable Manual"
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Asentar Comprobante"
      cancelText="Cancelar"
      confirmLoading={loading}
      width={920}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Space style={{ display: 'flex' }} size="middle">
          <Form.Item
            name="date"
            label="Fecha Contable"
            rules={[
              { required: true, message: 'Seleccione la fecha' },
              {
                validator: (_, val) => {
                  if (
                    val &&
                    lockDate &&
                    (val.isBefore(dayjs(lockDate), 'day') ||
                      val.isSame(dayjs(lockDate), 'day'))
                  ) {
                    return Promise.reject(
                      new Error(
                        `El periodo hasta el ${dayjs(lockDate).format(
                          'DD/MM/YYYY'
                        )} se encuentra cerrado.`
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra={
              lockDate ? (
                <Text type="danger" style={{ fontSize: 11 }}>
                  Cerrado hasta: {dayjs(lockDate).format('DD/MM/YYYY')}
                </Text>
              ) : null
            }
            style={{ width: 230 }}
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              disabledDate={(current) => {
                if (!lockDate || !current) return false;
                return (
                  current.isBefore(dayjs(lockDate), 'day') ||
                  current.isSame(dayjs(lockDate), 'day')
                );
              }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Concepto General del Asiento"
            rules={[
              { required: true, message: 'Ingrese el concepto o motivo del asiento' },
            ]}
            style={{ flex: 1 }}
          >
            <Input placeholder="Ej. Depreciación acumulada activos fijos mes actual" />
          </Form.Item>
        </Space>

        <Divider style={{ margin: '12px 0' }}>
          Partidas del Asiento (Débitos y Créditos)
        </Divider>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            maxHeight: 350,
            overflowY: 'auto',
          }}
        >
          {entryLines.map((line, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                background: '#fafafa',
                padding: '8px 12px',
                borderRadius: 6,
              }}
            >
              <div style={{ width: 280 }}>
                <Select
                  placeholder="Cuenta Contable"
                  showSearch
                  virtual={false}
                  optionFilterProp="children"
                  value={line.account_id}
                  onChange={(val) => handleLineChange(idx, 'account_id', val)}
                  style={{ width: '100%' }}
                >
                  {(Array.isArray(accounts) ? accounts : []).map((acc) => (
                    <Option key={acc.id} value={acc.id}>
                      {acc.code} - {acc.name}
                    </Option>
                  ))}
                </Select>
              </div>

              <div style={{ width: 180 }}>
                <Select
                  placeholder="Tercero (Opcional)"
                  showSearch
                  virtual={false}
                  optionFilterProp="children"
                  value={line.contact_id}
                  onChange={(val) => handleLineChange(idx, 'contact_id', val)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {(Array.isArray(contacts) ? contacts : []).map((c: any) => (
                    <Option key={c.id || c.uid} value={c.id || c.uid}>
                      {c.name} {c.document_number ? `(${c.document_number})` : ''}
                    </Option>
                  ))}
                </Select>
              </div>

              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Detalle de línea"
                  value={line.description}
                  onChange={(e) =>
                    handleLineChange(idx, 'description', e.target.value)
                  }
                />
              </div>

              <div style={{ width: 130 }}>
                <InputNumber
                  placeholder="Débito"
                  value={line.debit}
                  min={0}
                  precision={2}
                  onChange={(val) => handleLineChange(idx, 'debit', val || 0)}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ width: 130 }}>
                <InputNumber
                  placeholder="Crédito"
                  value={line.credit}
                  min={0}
                  precision={2}
                  onChange={(val) => handleLineChange(idx, 'credit', val || 0)}
                  style={{ width: '100%' }}
                />
              </div>

              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveLine(idx)}
              />
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            type="dashed"
            onClick={handleAddLine}
            icon={<PlusOutlined />}
          >
            Agregar Línea
          </Button>

          <Space size="large">
            <div>
              <Text type="secondary">Total Débito: </Text>
              <Text strong style={{ color: '#1677ff' }}>
                {formatCurrency(totalDebit)}
              </Text>
            </div>
            <div>
              <Text type="secondary">Total Crédito: </Text>
              <Text strong style={{ color: '#722ed1' }}>
                {formatCurrency(totalCredit)}
              </Text>
            </div>
            <div>
              <Text type="secondary">Diferencia: </Text>
              <Text
                strong
                style={{ color: difference === 0 ? '#52c41a' : '#ff4d4f' }}
              >
                {formatCurrency(difference)}
              </Text>
            </div>
          </Space>
        </div>

        <div style={{ marginTop: 12 }}>
          <Alert
            type={isBalanced ? 'success' : 'warning'}
            showIcon
            icon={isBalanced ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            message={
              isBalanced
                ? 'Partida doble balanceada y lista para asentar.'
                : `El comprobante no está cuadrado. La diferencia debe ser cero (actual: ${formatCurrency(
                    Math.abs(difference)
                  )}).`
            }
          />
        </div>
      </Form>
    </Modal>
  );
};
